import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, Crown, Star, Rocket, ExternalLink, Globe, Lock, LayoutDashboard, Megaphone, Home, Send, Archive, Eye, Pencil } from "lucide-react";
import { homesMyAgentAccount, homesSaveAgentAccount, homesFeaturedCities, homesMyListings, homesSetListingStatus } from "@/lib/api";
import { useAuth, login } from "@/context/AuthContext";

const TIER_META = {
  free: { name: "Free", icon: Rocket, color: "text-slate-500 bg-slate-100" },
  professional: { name: "Professional", icon: Star, color: "text-[#2563EB] bg-[#EEF2FF]" },
  local_expert: { name: "Local Expert", icon: Crown, color: "text-amber-600 bg-amber-100" },
};

const EMPTY = { name: "", title: "Real Estate Agent", brokerage: "", phone: "", email: "", website: "", bio: "", photo_url: "", city_geo_id: "", specialties: "", languages: "", published: false };

export default function AgentPortal() {
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [tier, setTier] = useState("free");
  const [slug, setSlug] = useState("");
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;                 // wait for auth to resolve first
    if (!user) { setLoading(false); return; }
    setLoading(true);
    homesFeaturedCities(60).then((d) => setCities(d.cities || [])).catch(() => {});
    homesMyAgentAccount().then((d) => {
      const a = d.account;
      if (a) {
        setTier(a.tier || "free"); setSlug(a.slug || "");
        setForm({
          name: a.name || "", title: a.title || "Real Estate Agent", brokerage: a.brokerage || "",
          phone: a.phone || "", email: a.email || "", website: a.website || "", bio: a.bio || "",
          photo_url: a.photo_url || "", city_geo_id: a.city_geo_id || "",
          specialties: (a.specialties || []).join(", "), languages: (a.languages || []).join(", "),
          published: !!a.published,
        });
      } else {
        setForm((f) => ({ ...f, name: user.name || "", email: user.email || "" }));
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user, authLoading]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const paid = tier === "professional" || tier === "local_expert";

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { account } = await homesSaveAgentAccount(payload);
      setTier(account.tier); setSlug(account.slug); setForm((f) => ({ ...f, published: account.published }));
      toast.success("Profile saved");
    } catch {
      toast.error("Could not save. Please try again.");
    } finally { setSaving(false); }
  };

  if (!user && authLoading) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></Center>;
  if (!user) return (
    <Center>
      <Lock className="h-10 w-10 text-slate-300" />
      <h1 className="mt-4 text-2xl font-black text-[#0F172A]">Sign in to manage your agent profile</h1>
      <button onClick={() => login("/homes/agent-portal")} className="mt-5 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-bold text-white hover:bg-[#1D4ED8]" data-testid="portal-signin">Sign in</button>
    </Center>
  );
  if (loading) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></Center>;

  const TM = TIER_META[tier] || TIER_META.free;
  const Icon = TM.icon;

  return (
    <div className="mx-auto max-w-[900px] px-4 py-10" data-testid="agent-portal">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black text-[#0F172A]">Agent Portal</h1>
          <p className="text-slate-500">Manage your HomesPulse profile and microsite.</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold ${TM.color}`} data-testid="portal-tier-badge">
          <Icon className="h-4 w-4" /> {TM.name} plan
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <Link to="/homes/list-property" className="flex items-center gap-2 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="portal-list-property-link">
          <Home className="h-4 w-4" /> List a Property
        </Link>
        <Link to="/homes/crm" className="flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-[#0F172A] transition-colors hover:bg-slate-50" data-testid="portal-crm-link">
          <LayoutDashboard className="h-4 w-4" /> Open CRM
        </Link>
        <Link to="/homes/marketing" className="flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-[#0F172A] transition-colors hover:bg-slate-50" data-testid="portal-marketing-link">
          <Megaphone className="h-4 w-4" /> Marketing Center
        </Link>
      </div>

      {!paid && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#2563EB]/30 bg-[#EEF2FF] p-5">
          <div>
            <div className="font-bold text-[#0F172A]">Unlock your hosted microsite</div>
            <div className="text-sm text-slate-600">Upgrade to Professional or Local Expert to publish a public agent page.</div>
          </div>
          <Link to="/homes/subscribe" className="rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#1D4ED8]" data-testid="portal-upgrade">Upgrade</Link>
        </div>
      )}

      {paid && slug && form.published && (
        <a href={`/homes/agent/${slug}`} target="_blank" rel="noreferrer" className="mt-5 flex items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 transition-colors hover:bg-emerald-100" data-testid="portal-microsite-link">
          <div className="flex items-center gap-2 text-emerald-800"><Globe className="h-5 w-5" /><span className="font-bold">Your microsite is live: /homes/agent/{slug}</span></div>
          <ExternalLink className="h-4 w-4 text-emerald-700" />
        </a>
      )}

      <MyListings />

      <div className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Full name *" v={form.name} onChange={set("name")} testid="portal-name" />
          <Field label="Title" v={form.title} onChange={set("title")} testid="portal-title" />
          <Field label="Brokerage" v={form.brokerage} onChange={set("brokerage")} testid="portal-brokerage" />
          <Field label="Phone" v={form.phone} onChange={set("phone")} testid="portal-phone" />
          <Field label="Email" v={form.email} onChange={set("email")} testid="portal-email" />
          <Field label="Website" v={form.website} onChange={set("website")} placeholder="yourwebsite.com" testid="portal-website" />
          <div>
            <Label>City / market</Label>
            <select value={form.city_geo_id} onChange={set("city_geo_id")} className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-[#0F172A] outline-none focus:border-[#2563EB]" data-testid="portal-city">
              <option value="">Select a city…</option>
              {cities.map((c) => <option key={c.geo_id} value={c.geo_id}>{`${c.name}, ${c.state_name}`}</option>)}
            </select>
          </div>
          <Field label="Photo URL" v={form.photo_url} onChange={set("photo_url")} placeholder="https://…" testid="portal-photo" />
        </div>
        <div>
          <Label>Bio</Label>
          <textarea value={form.bio} onChange={set("bio")} rows={4} className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-[#0F172A] outline-none focus:border-[#2563EB]" data-testid="portal-bio" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Specialties (comma-separated)" v={form.specialties} onChange={set("specialties")} placeholder="Condos, First-time buyers" testid="portal-specialties" />
          <Field label="Languages (comma-separated)" v={form.languages} onChange={set("languages")} placeholder="English, Spanish" testid="portal-languages" />
        </div>

        <label className={`flex items-center gap-3 rounded-xl border p-3.5 ${paid ? "border-slate-200" : "border-slate-100 bg-slate-50"}`}>
          <input type="checkbox" checked={form.published} disabled={!paid} onChange={set("published")} className="h-4 w-4 accent-[#2563EB]" data-testid="portal-publish-toggle" />
          <span className="text-sm font-semibold text-[#0F172A]">Publish my public microsite {!paid && <span className="font-normal text-slate-400">(requires a paid plan)</span>}</span>
        </label>

        <button onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60" data-testid="portal-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save profile
        </button>
      </div>
    </div>
  );
}

const Label = ({ children }) => <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">{children}</label>;
const Field = ({ label, v, onChange, placeholder, testid }) => (
  <div><Label>{label}</Label><input value={v} onChange={onChange} placeholder={placeholder} className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-[#0F172A] outline-none focus:border-[#2563EB]" data-testid={testid} /></div>
);
const Center = ({ children }) => <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">{children}</div>;

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-700",
  coming_soon: "bg-blue-100 text-blue-700",
  active_under_contract: "bg-amber-100 text-amber-700",
  pending: "bg-amber-100 text-amber-700",
  draft: "bg-slate-100 text-slate-600",
  withdrawn: "bg-slate-100 text-slate-600",
  temporarily_off_market: "bg-slate-100 text-slate-600",
  sold: "bg-slate-200 text-slate-700",
  rented: "bg-slate-200 text-slate-700",
  expired: "bg-slate-100 text-slate-500",
};
const statusLabel = (s) => (s || "active").replace(/_/g, " ");

function MyListings() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => homesMyListings().then((d) => setItems(d.listings || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, listing_status) => {
    setBusy(id);
    try {
      await homesSetListingStatus(id, listing_status);
      toast.success(listing_status === "active" ? "Listing published" : `Marked ${statusLabel(listing_status)}`);
      await load();
    } catch {
      toast.error("Could not update the listing.");
    } finally { setBusy(""); }
  };

  if (items === null) return null;
  if (items.length === 0) return null;

  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="my-listings">
      <h2 className="text-xl font-black text-[#0F172A]">My listings</h2>
      <p className="text-sm text-slate-500">Publish drafts, or take a live listing off the market.</p>
      <div className="mt-4 space-y-3">
        {items.map((l) => {
          const st = l.listing_status || (l.published === false ? "draft" : "active");
          const isDraftLike = !l.published;
          return (
            <div key={l.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 p-3.5" data-testid={`my-listing-${l.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-bold text-[#0F172A]">{l.headline || l.address || "Untitled listing"}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLE[st] || "bg-slate-100 text-slate-600"}`} data-testid={`my-listing-status-${l.id}`}>{statusLabel(st)}</span>
                </div>
                <div className="truncate text-xs text-slate-500">{[l.address, l.city_name, l.state_name].filter(Boolean).join(", ")} · ${Number(l.price || 0).toLocaleString()}{l.status === "for_rent" ? "/mo" : ""}</div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Link to={`/homes/list-property?edit=${l.id}`} className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-[#0F172A] hover:bg-slate-50" data-testid={`my-listing-edit-${l.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Link>
                {l.published && (
                  <Link to={`/homes/property/${l.id}`} className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-[#0F172A] hover:bg-slate-50" data-testid={`my-listing-view-${l.id}`}>
                    <Eye className="h-3.5 w-3.5" /> View
                  </Link>
                )}
                {isDraftLike ? (
                  <button onClick={() => changeStatus(l.id, "active")} disabled={busy === l.id} className="flex items-center gap-1 rounded-lg bg-[#2563EB] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#1D4ED8] disabled:opacity-60" data-testid={`my-listing-publish-${l.id}`}>
                    {busy === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {st === "draft" || st === "coming_soon" ? "Publish" : "Re-list"}
                  </button>
                ) : (
                  <button onClick={() => changeStatus(l.id, "withdrawn")} disabled={busy === l.id} className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-[#0F172A] hover:bg-slate-50 disabled:opacity-60" data-testid={`my-listing-withdraw-${l.id}`}>
                    {busy === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />} Take off market
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
